import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { getInsuranceFlowBackgroundColor } from 'src/app/_enums/insurance-flow/insurance-flow-status.enum';
import { InsuranceFlowDetailsResponse } from 'src/app/_types/insurance-flow.types';
import { CreateLeadResponse } from 'src/app/_types/proposals.type';
import { SlideInModalComponent } from 'src/app/shared/components/slide-in-modal/slide-in-modal.component';

import { TaskDetailsBodyComponent } from './components/task-details-body/task-details-body.component';
import { TaskDetailsHeaderActionsComponent } from './components/task-details-header-actions/task-details-header-actions.component';
import { TaskDetailsHeaderInfoComponent } from './components/task-details-header-info/task-details-header-info.component';
import { TaskDetailsService } from './task-details.service';

/**
 * Slide-in modal host for the task details view.
 * The same content is rendered as a standalone page by TaskDetailsPageComponent.
 */
@Component({
  selector: 'app-task-details',
  standalone: true,
  imports: [
    SlideInModalComponent,
    TaskDetailsHeaderInfoComponent,
    TaskDetailsHeaderActionsComponent,
    TaskDetailsBodyComponent,
  ],
  templateUrl: './task-details.component.html',
  styleUrl: './task-details.component.css',
  // We want each instance of the component and its children to have its own mediator service
  providers: [TaskDetailsService],
})
export class TaskDetailsComponent implements OnInit, OnDestroy {
  id: string | null = null;

  private taskStatusSubscription?: Subscription;

  private changedSubscription?: Subscription;

  @Output() updated = new EventEmitter();

  /** Passed to the slide-in: raised when this modal opens above another one. */
  @Input() layer = 0;

  @ViewChild('modal') modal?: SlideInModalComponent;

  /** Set on the first « Voir l'affaire »; that is what builds the stacked modal. */
  protected stackedFlowId: string | null = null;

  private stackedModal?: TaskDetailsComponent;

  // The stacked modal exists only once `stackedFlowId` is set, so its first
  // opening happens here, when the view hands it over.
  @ViewChild('stackedModal')
  private set stacked(modal: TaskDetailsComponent | undefined) {
    this.stackedModal = modal;
    if (modal && this.stackedFlowId) modal.openModal(this.stackedFlowId);
  }

  constructor(private readonly taskDetailsService: TaskDetailsService) {}

  ngOnInit(): void {
    // emits updated only when the status changes
    this.taskStatusSubscription =
      this.taskDetailsService.statusChanged$.subscribe(() =>
        this.updated.emit()
      );
    // A change of its own the affaire's status does not carry: a cancellation
    // opened on its policy leaves the policy exactly where it was.
    this.changedSubscription = this.taskDetailsService.changed$.subscribe(() =>
      this.updated.emit()
    );
  }

  ngOnDestroy(): void {
    this.taskStatusSubscription?.unsubscribe();
    this.changedSubscription?.unsubscribe();
  }

  openModal(id: string): void {
    this.id = id;
    this.taskDetailsService.loadTaskDetails(id);
    if (this.modal) {
      this.modal.openModal();
    }
  }

  closeModal(): void {
    this.modal?.closeModal(false);
  }

  /** Another flow's modal above this one — the replacement of a cancellation, say. */
  openStackedFlow(flowId: string): void {
    this.stackedFlowId = flowId;
    // Already built from an earlier click: reopen it, on this flow.
    this.stackedModal?.openModal(flowId);
  }

  getInsuranceFlowBackgroundColor = getInsuranceFlowBackgroundColor;

  protected get insuranceFlowData(): InsuranceFlowDetailsResponse | null {
    return this.taskDetailsService.state.insuranceFlowData;
  }

  refreshTaskDetails(emit = false) {
    this.taskDetailsService.refreshTaskDetails();
    if (emit) this.updated.emit();
  }

  onLeadCreated({ flowId }: CreateLeadResponse): void {
    // The modal re-targets in place: navigating would tear down the list it was opened from.
    // The page does the opposite and navigates, to keep its URL in sync with the displayed flow.
    this.openModal(flowId);
    this.updated.emit();
  }
}
